import { useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useSiteCtaStyles } from '../hooks/useSiteCtaStyles';

/** Word count above which the body is clamped with a Read More toggle.
 *  Matches PropertyOverview › blurb. */
const BODY_CLAMP_WORDS = 1000;
const HTML_TAG_RE = /<[^>]+>/g;

/** Plain-text word count from richtext HTML (tags ignored). */
function countWords(html: string): number {
  const text = html.replace(HTML_TAG_RE, ' ').replace(/\s+/g, ' ').trim();
  if (!text) return 0;
  return text.split(' ').length;
}

// ============================================================================
// Four highlighted amenity cards. Title is selected from the amenities
// collection; image is then selected from photo_gallery to depict that same
// amenity (existing photos only). Definitions are fully inlined for static
// schema extraction.
// ============================================================================

const amenity1Def: SlotGroupDefinition = {
  label: 'Highlighted Amenity 1',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[0].image',
      generator: {
        prompt:
          'From photo_gallery only, pick the existing photo whose subject best matches the amenity title on this card (e.g. pool title → pool photo, fitness title → gym photo). The image must depict that amenity — not a generic or unrelated space. Never invent an image.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
      generator: {
        prompt:
          'Alt text for the selected gallery photo that names the amenity shown.',
      },
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultField: 'amenities[0].title',
      defaultValue: 'Fitness Center',
      suggestedFields: ['amenities[0].title', 'amenities[1].title', 'amenities[2].title'],
      generator: {
        prompt:
          'Select the amenity title exactly from the property amenities collection (amenities.title).',
      },
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: {
      image: 'image',
      alt: 'alt_text',
    },
  },
  generator: {
    prompt:
      'First choose a strong amenity from the amenities collection, then choose a photo_gallery image that clearly shows that same amenity. Title and image must match (pool with pool, lounge with lounge, etc.). Prefer amenities that have a clear matching gallery photo. Skip amenities with no visual match and skip basic utility amenities.',
    context:
      'Home highlighted amenities, card 1 of 4. Title from amenities; image from photo_gallery matched to that title. Each card must use a different amenity and a different gallery photo.',
  },
};

const amenity2Def: SlotGroupDefinition = {
  label: 'Highlighted Amenity 2',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[1].image',
      generator: {
        prompt:
          'From photo_gallery only, pick the existing photo whose subject best matches the amenity title on this card (e.g. pool title → pool photo, fitness title → gym photo). The image must depict that amenity — not a generic or unrelated space. Never invent an image.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[1].alt_text',
      generator: {
        prompt:
          'Alt text for the selected gallery photo that names the amenity shown.',
      },
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultField: 'amenities[1].title',
      defaultValue: 'Resident Lounge',
      suggestedFields: ['amenities[0].title', 'amenities[1].title', 'amenities[2].title'],
      generator: {
        prompt:
          'Select the amenity title exactly from the property amenities collection (amenities.title).',
      },
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: {
      image: 'image',
      alt: 'alt_text',
    },
  },
  generator: {
    prompt:
      'First choose a strong amenity from the amenities collection, then choose a photo_gallery image that clearly shows that same amenity. Title and image must match (pool with pool, lounge with lounge, etc.). Prefer amenities that have a clear matching gallery photo. Skip amenities with no visual match and skip basic utility amenities.',
    context:
      'Home highlighted amenities, card 2 of 4. Title from amenities; image from photo_gallery matched to that title. Each card must use a different amenity and a different gallery photo.',
  },
};

const amenity3Def: SlotGroupDefinition = {
  label: 'Highlighted Amenity 3',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[2].image',
      generator: {
        prompt:
          'From photo_gallery only, pick the existing photo whose subject best matches the amenity title on this card (e.g. pool title → pool photo, fitness title → gym photo). The image must depict that amenity — not a generic or unrelated space. Never invent an image.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[2].alt_text',
      generator: {
        prompt:
          'Alt text for the selected gallery photo that names the amenity shown.',
      },
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultField: 'amenities[2].title',
      defaultValue: 'Study Spaces',
      suggestedFields: ['amenities[0].title', 'amenities[1].title', 'amenities[2].title'],
      generator: {
        prompt:
          'Select the amenity title exactly from the property amenities collection (amenities.title).',
      },
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: {
      image: 'image',
      alt: 'alt_text',
    },
  },
  generator: {
    prompt:
      'First choose a strong amenity from the amenities collection, then choose a photo_gallery image that clearly shows that same amenity. Title and image must match (pool with pool, lounge with lounge, etc.). Prefer amenities that have a clear matching gallery photo. Skip amenities with no visual match and skip basic utility amenities.',
    context:
      'Home highlighted amenities, card 3 of 4. Title from amenities; image from photo_gallery matched to that title. Each card must use a different amenity and a different gallery photo.',
  },
};

const amenity4Def: SlotGroupDefinition = {
  label: 'Highlighted Amenity 4',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[3].image',
      generator: {
        prompt:
          'From photo_gallery only, pick the existing photo whose subject best matches the amenity title on this card (e.g. pool title → pool photo, fitness title → gym photo). The image must depict that amenity — not a generic or unrelated space. Never invent an image.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[3].alt_text',
      generator: {
        prompt:
          'Alt text for the selected gallery photo that names the amenity shown.',
      },
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultField: 'amenities[3].title',
      defaultValue: 'Resort-Style Pool',
      suggestedFields: ['amenities[0].title', 'amenities[1].title', 'amenities[2].title'],
      generator: {
        prompt:
          'Select the amenity title exactly from the property amenities collection (amenities.title).',
      },
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: {
      image: 'image',
      alt: 'alt_text',
    },
  },
  generator: {
    prompt:
      'First choose a strong amenity from the amenities collection, then choose a photo_gallery image that clearly shows that same amenity. Title and image must match (pool with pool, lounge with lounge, etc.). Prefer amenities that have a clear matching gallery photo. Skip amenities with no visual match and skip basic utility amenities.',
    context:
      'Home highlighted amenities, card 4 of 4. Title from amenities; image from photo_gallery matched to that title. Each card must use a different amenity and a different gallery photo.',
  },
};

// Primary "View All Amenities" CTA — links to the /amenities page by default.
// Editors can override the URL / label / target.
const ctaLinkDef: SlotGroupDefinition = {
  label: 'View All Amenities Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'View All Amenities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function AmenityTile({
  image,
  alt,
  title,
  href,
  target,
  priority,
}: {
  image?: string | null;
  alt?: string | null;
  title?: string | null;
  href: string;
  target?: string | null;
  priority?: boolean;
}) {
  const src = image || '';
  const label = (title || '').trim();
  const imgAlt = (alt || label || '').trim();

  return (
    <a
      data-reveal
      className="afy-tile-inner"
      role="listitem"
      href={href}
      target={target || '_self'}
      aria-label={label ? `${label} — view all amenities` : 'View all amenities'}
    >
      <div className="afy-frame">
        {src ? (
          <VibesitesImage
            url={src}
            alt={imgAlt}
            sizes="(min-width: 1024px) 22vw, (min-width: 640px) 40vw, 45vw"
            fill
            objectFit="cover"
            className="afy-img"
            priority={priority}
          />
        ) : (
          <div className="afy-img afy-img--empty" aria-hidden="true" />
        )}
        <div className="afy-overlay">
          {label ? <span className="afy-label">{label}</span> : null}
        </div>
      </div>
    </a>
  );
}

export function AmenitiesForYou() {
  const amenity1 = useSlotGroup('amenitiesforyou.amenity_1', amenity1Def);
  const amenity2 = useSlotGroup('amenitiesforyou.amenity_2', amenity2Def);
  const amenity3 = useSlotGroup('amenitiesforyou.amenity_3', amenity3Def);
  const amenity4 = useSlotGroup('amenitiesforyou.amenity_4', amenity4Def);
  const cta = useSlotGroup('amenitiesforyou.cta_link', ctaLinkDef);
  const rawBody = useContentToken('amenitiesforyou.body');
  const [bodyExpanded, setBodyExpanded] = useState(false);
  const isLongBody = countWords(String(rawBody || '')) > BODY_CLAMP_WORDS;

  useRevealStyles();
  useSiteCtaStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const galleryRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 90 });

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up amenities-for-you w-full overflow-hidden bg-background py-14 md:py-20 lg:py-24"
    >
      <div className="mx-auto flex w-full max-w-6xl flex-col items-center gap-10 px-4 sm:gap-12 sm:px-6 lg:gap-14 lg:px-8">
        <div className="afy-intro flex w-full flex-col items-center gap-5 sm:gap-6">
          <div className="afy-heading-wrap">
            <span className="afy-heading-rule" aria-hidden="true" />
            <RichTextContent
              path="amenitiesforyou.heading"
              label="Heading"
              defaultValue="<p>A Space For Everyone</p>"
              as="h2"
              className="afy-heading"
            />
            <span className="afy-heading-rule" aria-hidden="true" />
          </div>

          <div
            className={`afy-body-wrap ${
              bodyExpanded || !isLongBody ? 'afy-body-wrap--open' : ''
            }`}
          >
            <RichTextContent
              path="amenitiesforyou.body"
              label="Body Text"
              as="div"
              className="afy-body font-body"
            />
          </div>
          {isLongBody && (
            <button
              type="button"
              onClick={() => setBodyExpanded((v) => !v)}
              aria-expanded={bodyExpanded}
              className="afy-read-more font-body"
            >
              {bodyExpanded ? (
                <RichTextContent path="amenitiesforyou.read_less_label" as="span" />
              ) : (
                <RichTextContent path="amenitiesforyou.read_more_label" as="span" />
              )}
            </button>
          )}
        </div>

        <div ref={galleryRef} className="afy-gallery" role="list">
          <EditableSlotGroup
            groupPath="amenitiesforyou.amenity_1"
            definition={amenity1Def}
            className="afy-tile afy-tile--up"
          >
            <AmenityTile
              image={amenity1.image}
              alt={amenity1.alt}
              title={amenity1.title}
              href={cta.url || '/amenities'}
              target={cta._target}
              priority
            />
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="amenitiesforyou.amenity_2"
            definition={amenity2Def}
            className="afy-tile afy-tile--down"
          >
            <AmenityTile
              image={amenity2.image}
              alt={amenity2.alt}
              title={amenity2.title}
              href={cta.url || '/amenities'}
              target={cta._target}
            />
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="amenitiesforyou.amenity_3"
            definition={amenity3Def}
            className="afy-tile afy-tile--up"
          >
            <AmenityTile
              image={amenity3.image}
              alt={amenity3.alt}
              title={amenity3.title}
              href={cta.url || '/amenities'}
              target={cta._target}
            />
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="amenitiesforyou.amenity_4"
            definition={amenity4Def}
            className="afy-tile afy-tile--down"
          >
            <AmenityTile
              image={amenity4.image}
              alt={amenity4.alt}
              title={amenity4.title}
              href={cta.url || '/amenities'}
              target={cta._target}
            />
          </EditableSlotGroup>
        </div>

        <div className="afy-cta-row flex w-full justify-center">
          <EditableSlotGroup
            groupPath="amenitiesforyou.cta_link"
            definition={ctaLinkDef}
          >
            <a
              href={cta.url || '/amenities'}
              target={cta._target || '_self'}
              className="site-cta site-cta--secondary"
            >
              {cta.label || 'View All Amenities'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* Match PropertyOverview › cta_secondary: filled primary at rest,
           surface fill + contrast ink on hover. */
        .afy-cta-row {
          --site-cta-secondary-accent: var(--vibe-color-primary);
          --site-cta-secondary-on-accent: var(--site-cta-ink-on-primary, var(--vibe-color-text-on-primary, #ffffff));
          --site-cta-hover-accent: var(--vibe-color-surface);
          --site-cta-hover-on-accent: var(--site-cta-ink-on-surface, #ffffff);
        }

        .afy-heading-wrap {
          display: flex;
          align-items: center;
          gap: clamp(0.85rem, 2.5vw, 1.75rem);
          width: 100%;
        }
        .afy-heading-rule {
          flex: 1 1 auto;
          height: 1px;
          min-width: 1.5rem;
          background: color-mix(in srgb, var(--vibe-color-text) 18%, transparent);
        }
        .afy-heading {
          flex: 0 1 auto;
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.75rem, 3.5vw, 2.5rem);
          font-weight: 700;
          line-height: 1.15;
          margin: 0;
          text-align: center;
          color: var(--vibe-color-primary);
          white-space: nowrap;
        }
        .afy-heading [data-vibesite-richtext] p {
          margin: 0;
        }

        /* Body typography + clamp match PropertyOverview › blurb. */
        .afy-body-wrap {
          max-width: 40rem;
          max-height: 9.6em;
          overflow: hidden;
          margin: 0;
          transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          -webkit-mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
          mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
        }
        .afy-body-wrap:has(+ .afy-read-more) {
          margin-bottom: 0;
        }
        .afy-body-wrap--open {
          max-height: none;
          -webkit-mask-image: none;
          mask-image: none;
        }
        /* Match PropertyOverview › blurb: font-body class owns the family
           (no extra font-family here — that fought Tailwind and showed a
           different typeface). Inner p/span inherit so richtext marks
           cannot swap the face. */
        .afy-body {
          margin: 0;
          font-size: 1.0625rem;
          line-height: 1.7;
          color: var(--vibe-color-text);
          text-align: center;
        }
        .afy-body p,
        .afy-body span {
          color: inherit;
          font: inherit;
        }
        .afy-body p {
          margin: 0 0 1.15rem;
        }
        .afy-body p:last-child {
          margin-bottom: 0;
        }
        .afy-body a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }
        .afy-read-more {
          display: inline-block;
          margin: 0.75rem 0 0;
          padding: 0;
          border: 0;
          background: transparent;
          font-size: 0.875rem;
          font-weight: 600;
          letter-spacing: 0.02em;
          color: var(--vibe-color-primary);
          cursor: pointer;
          transition: color 0.2s ease;
        }
        .afy-read-more:hover {
          text-decoration: underline;
        }
        .afy-read-more p,
        .afy-read-more span {
          margin: 0;
          color: inherit;
          font: inherit;
        }

        .afy-gallery {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: clamp(0.65rem, 1.6vw, 1rem);
          width: 100%;
          align-items: start;
        }

        .afy-tile {
          display: block;
          min-width: 0;
        }
        .afy-tile--up {
          margin-top: 0;
        }
        .afy-tile--down {
          margin-top: clamp(1.75rem, 4vw, 3rem);
        }
        .afy-tile-inner {
          display: block;
          width: 100%;
          text-decoration: none;
          color: inherit;
          cursor: pointer;
        }

        .afy-frame {
          position: relative;
          width: 100%;
          aspect-ratio: 1 / 1;
          overflow: hidden;
          border-radius: 15px;
          background: var(--vibe-color-surface);
        }

        .afy-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
          transition: transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .afy-img--empty {
          background: color-mix(in srgb, var(--vibe-color-primary) 8%, var(--vibe-color-surface));
        }

        .afy-overlay {
          position: absolute;
          inset: 0;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          background: linear-gradient(
            to top,
            rgba(0, 0, 0, 0.45) 0%,
            rgba(0, 0, 0, 0.18) 45%,
            rgba(0, 0, 0, 0.22) 100%
          );
          pointer-events: none;
        }

        .afy-label {
          font-family: var(--vibe-font-heading);
          font-size: clamp(0.95rem, 1.4vw, 1.2rem);
          font-weight: 600;
          line-height: 1.25;
          letter-spacing: 0.01em;
          text-align: center;
          color: #ffffff;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
          max-width: 14ch;
        }

        @media (hover: hover) and (pointer: fine) {
          .afy-tile:hover .afy-img:not(.afy-img--empty) {
            transform: scale(1.04);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .afy-img {
            transition: none;
          }
        }

        @media (max-width: 767px) {
          .afy-gallery {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 0.65rem;
          }
          .afy-tile--down {
            margin-top: 1.25rem;
          }
          .afy-heading {
            white-space: normal;
            max-width: 16ch;
          }
          .afy-label {
            font-size: 0.95rem;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesForYou',
  displayName: 'Amenities For You',
  tokens: {
    'amenitiesforyou.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>A Space For Everyone</p>',
    },
    'amenitiesforyou.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_home_amenities_blurb',
      defaultValue:
        "<p>Student living is effortless with amenities that support both focus and fun. Stay on top of your studies with high-speed Wi-Fi, private study lounges, and a fully equipped business center. When it's time to recharge, relax at the resort-style pool, break a sweat in the 24-hour fitness center, or connect with friends in our lively social spaces.</p>",
    },
    'amenitiesforyou.read_more_label': {
      label: 'Read More Label',
      type: 'richtext',
      defaultValue: '<p>Read More</p>',
    },
    'amenitiesforyou.read_less_label': {
      label: 'Read Less Label',
      type: 'richtext',
      defaultValue: '<p>Read Less</p>',
    },
  },
  slotGroups: {
    'amenitiesforyou.amenity_1': amenity1Def,
    'amenitiesforyou.amenity_2': amenity2Def,
    'amenitiesforyou.amenity_3': amenity3Def,
    'amenitiesforyou.amenity_4': amenity4Def,
    'amenitiesforyou.cta_link': ctaLinkDef,
  },
};

(AmenitiesForYou as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenitiesForYou;
